import {
  action$,
  actionCreator,
  reducer,
  reduceState,
  streamReducer,
  toPersistentStream,
} from '@ardoq/rxbeach';
import { ArdoqId, ViewIds } from '@ardoq/api-types';
import { combineLatest } from 'rxjs';
import { ActiveViews, activeView$ } from './views/mainContent/activeView$';
import { filter, map, pairwise } from 'rxjs/operators';
import { isTruthy } from '@ardoq/common-helpers';

type ViewIdToComponentIds = Partial<Record<ViewIds, ArdoqId[]>>;

export const readViewComponentIds = actionCreator<ViewIdToComponentIds>(
  '[viewComponentIds$] READ_VIEW_COMPONENT_IDS'
);

const handleReadViewComponentIdsReducer = (
  state: ViewIdToComponentIds,
  update: ViewIdToComponentIds
) => ({ ...state, ...update });

const toMountedViewIds = ({
  mainViewId,
  secondaryViewId,
}: ActiveViews): ViewIds[] => [mainViewId, secondaryViewId].filter(isTruthy);

export const findReplacedViewIds = ([previous, current]: [
  ActiveViews,
  ActiveViews,
]): ViewIds[] => {
  const mountedViewIds = toMountedViewIds(current);
  return toMountedViewIds(previous).filter(
    viewId => !mountedViewIds.includes(viewId)
  );
};

const replacedViewIds$ = activeView$.pipe(
  pairwise(),
  map(findReplacedViewIds),
  filter(replacedViewIds => replacedViewIds.length > 0)
);

const clearComponentIdsOfReplacedViews = (
  state: ViewIdToComponentIds,
  replacedViewIds: ViewIds[]
): ViewIdToComponentIds =>
  replacedViewIds.reduce(
    (nextState, viewId) => ({ ...nextState, [viewId]: [] }),
    state
  );

const defaultState: ViewIdToComponentIds = {};

const viewIdToComponentIds$ = action$.pipe(
  reduceState<ViewIdToComponentIds>('viewIdToComponentIds$', defaultState, [
    reducer(readViewComponentIds, handleReadViewComponentIdsReducer),
    streamReducer(replacedViewIds$, clearComponentIdsOfReplacedViews),
  ])
);

const viewComponentIds$ = toPersistentStream<ArdoqId[]>(
  'viewComponentIds$',
  combineLatest([viewIdToComponentIds$, activeView$]).pipe(
    map(
      ([viewIdToComponentIds, { mainViewId }]) =>
        viewIdToComponentIds[mainViewId] || []
    )
  ),
  []
);

export { viewComponentIds$ };
